/**
 * Push Notification utility — handles service worker registration,
 * subscription management, and browser permission state.
 */

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const PUSH_CLIENT_ID_KEY = 'ensue_push_client_id';

export function getPushClientId() {
  try {
    let id = localStorage.getItem(PUSH_CLIENT_ID_KEY);
    if (!id) {
      const randomPart =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      id = `web_${randomPart}`;
      localStorage.setItem(PUSH_CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `web_ephemeral_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPermissionState() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

const LEGACY_FCM_PREFIX = 'https://fcm.googleapis.com/fcm/send/';
const MIGRATION_PENDING_KEY = 'ensue_push_migration_pending';
const MIGRATION_DONE_KEY = 'ensue_push_legacy_migrated';

async function fetchVapidKey(api) {
  if (VAPID_PUBLIC_KEY) return VAPID_PUBLIC_KEY;
  const res = await api.get('/push/vapid-key');
  return res.data.public_key;
}

async function registerWithBackend(api, subscription) {
  await api.post('/push/subscribe', {
    subscription_data: subscription.toJSON(),
    platform: 'web',
    device_label: detectDeviceLabel(),
    push_client_id: getPushClientId(),
  });
}

/**
 * Legacy FCM endpoints (fcm.googleapis.com/fcm/send/…) ignore the Web Push
 * Urgency header, so incoming-call pushes get deferred by Android Doze until
 * the screen wakes. Re-subscribing on modern Chrome yields a /wp/ endpoint
 * that honors high urgency. Silent — requires permission already granted.
 */
export async function migrateLegacySubscription(api) {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  try {
    // Retry after a browser update — a newer browser may issue a modern endpoint
    if (localStorage.getItem(MIGRATION_DONE_KEY) === navigator.userAgent) return false;
  } catch {}
  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    let pending = false;
    try { pending = !!localStorage.getItem(MIGRATION_PENDING_KEY); } catch {}
    if (existing && !existing.endpoint.startsWith(LEGACY_FCM_PREFIX)) return false;
    if (!existing && !pending) return false; // user intentionally unsubscribed — leave alone
    const vapidKey = await fetchVapidKey(api);
    const oldEndpoint = existing?.endpoint || null;
    try { localStorage.setItem(MIGRATION_PENDING_KEY, '1'); } catch {}
    if (existing) await existing.unsubscribe();
    const fresh = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    await registerWithBackend(api, fresh);
    if (oldEndpoint && oldEndpoint !== fresh.endpoint) {
      try { await api.post('/push/unsubscribe-endpoint', { endpoint: oldEndpoint }); } catch {}
    }
    try {
      localStorage.removeItem(MIGRATION_PENDING_KEY);
      if (fresh.endpoint.startsWith(LEGACY_FCM_PREFIX)) {
        localStorage.setItem(MIGRATION_DONE_KEY, navigator.userAgent); // browser still issues legacy — retry only after it updates
      }
    } catch {}
    return true;
  } catch (err) {
    console.error('Legacy push migration failed:', err);
    return false;
  }
}

export async function subscribeToPush(api) {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Push notification permission denied');
  }

  const registration = await navigator.serviceWorker.ready;

  const vapidKey = await fetchVapidKey(api);

  let subscription = await registration.pushManager.getSubscription();
  if (subscription && subscription.endpoint.startsWith(LEGACY_FCM_PREFIX)) {
    // Replace legacy endpoint — it ignores the Urgency header (slow call pushes on Android)
    const oldEndpoint = subscription.endpoint;
    await subscription.unsubscribe();
    try { await api.post('/push/unsubscribe-endpoint', { endpoint: oldEndpoint }); } catch {}
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const subscriptionJSON = subscription.toJSON();

  // Detect browser/device label
  const deviceLabel = detectDeviceLabel();

  // Send subscription to backend
  await api.post('/push/subscribe', {
    subscription_data: subscriptionJSON,
    platform: 'web',
    device_label: deviceLabel,
    push_client_id: getPushClientId(),
  });

  return subscription;
}

export async function unsubscribeFromPush(api) {
  const subscription = await getExistingSubscription();
  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    try {
      await api.post('/push/unsubscribe-endpoint', { endpoint });
    } catch {
      // Best effort cleanup on server
    }
  }
}

function detectDeviceLabel() {
  const ua = navigator.userAgent;
  let browser = 'Browser';
  if (ua.includes('SamsungBrowser')) browser = 'Samsung Internet';
  else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';

  let os = 'Unknown';
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) os = 'iOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS') || ua.includes('Macintosh')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';

  return `${browser} on ${os}`;
}
